import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Logout } from "../store/slices/loginSlice";
import { SidebarProvider } from "../context/SidebarContext";
import Sidebar from "../components/Sider/Sider";
import Dashboard from "../pages/Dashboard/dashboard";
import SignIn from "../pages/SignIn/SignIn";
import ChangePassword from "../pages/ChangePassword/ChangePassword";
import CampaignList from "../pages/CampaignList/CampaignList";
import Reporting from "../pages/Reporting/Reporting";
import User from "../pages/UserManage/UserManage";
import Role from "../pages/RoleManage/RoleManage";
import Import from "../pages/ImportData/import";
import CampaignWinner from "../pages/CampaignWinner/CampaignWinner";
import Audit from "../pages/Audit/audit";
import TemplateMapping from "../pages/TempleteMapping/templatemapping"
import Transaction from "../pages/Transaction/transaction"
import ScreenMaker from "../pages/ScreenMaker/screenmaker"
import { PERMISSIONS } from "../utils/constant/permissions";
import ProtectedRoute from "./ProtectedRoute";
import { useSidebar } from "../context/SidebarContext";
import AdConfig from "../pages/AdConfigrations/adConfig";
import Notify from "../pages/notifyConfig/notifyConfig"
import PasswordPolicy from "../pages/PasswordPolicy/PasswordPolicy"
import SmtpConfig from "../pages/SmtpConfig/SmtpConfig"
import Blacklist from "../pages/Blacklist/Blacklist"
import Institution from "../pages/Institution/Institution"
import { useIdleTimer } from "../utils/hooks/useIdleTimer";
import IdleLogoutModal from "../components/IdleLogoutModal/IdleLogoutModal";





const adminPermissions = [
  PERMISSIONS.USER_VIEW,
  PERMISSIONS.ACTIVITY_LOG_VIEW,
  PERMISSIONS.CAMPAIGN_VIEW,
  PERMISSIONS.CAMPAIGN_WINNER_VIEW,
  PERMISSIONS.TRANSACTION_VIEW,
  PERMISSIONS.AUDIT_REPORT_VIEW,
  PERMISSIONS.CSV_TEMPLATE_VIEW,
  PERMISSIONS.ROLE_VIEW,
  PERMISSIONS.TRX_UPLOAD_VIEW,
  PERMISSIONS.AD_CONFIG_VIEW,
  PERMISSIONS.USER_CONFIG_VIEW,
  PERMISSIONS.NOTIFICATION_CONFIG_UPSERT,
  PERMISSIONS.PASSWORD_POLICY_VIEW,
]


const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { collapsed, isMobile, toggleSidebar } = useSidebar();
  const sidebarOpen = isMobile && !collapsed;
  const [idleModalOpen, setIdleModalOpen] = useState(false);

  useIdleTimer(() => setIdleModalOpen(true));

  const handleLoginAgain = async () => {
    setIdleModalOpen(false);
    await dispatch(Logout() as any);
    window.location.href = '/';
  };

  return (
    <div className="app-layout">
      {sidebarOpen && <div className="mobile-overlay" onClick={toggleSidebar} />}
      <div className={`sidebar-wrapper ${collapsed ? 'collapsed' : ''} ${sidebarOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>
      <main className={`content-wrapper ${collapsed ? 'collapsed' : ''}`}>
        {children}
      </main>
      <IdleLogoutModal open={idleModalOpen} onLoginAgain={handleLoginAgain} />
    </div>
  );
};

const ForcePasswordChangeGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const passwordChangeRequired = localStorage.getItem('passwordChangeRequired');
  if (passwordChangeRequired && location.pathname !== '/change-password' && location.pathname !== '/') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
};

const ConditionalLayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const noSidebarPaths = ['/', '/change-password'];
  const isNoSidebar = noSidebarPaths.includes(location.pathname);
  return isNoSidebar ? <>{children}</> : <AppLayout>{children}</AppLayout>;
};

function Routing() {
  const roleId = localStorage.getItem("roleId");

  return (
    <SidebarProvider>
      <Router>
        <ConditionalLayoutWrapper>
        <ForcePasswordChangeGuard>
        <Routes>
          <Route path="/" element={<SignIn />} />
          <Route path="/change-password" element={<ChangePassword />} />

          <>
            <Route path="/dashboard" element={
              <Dashboard />
            }

            />
            <Route path="/CampaignWinner" element={
              <ProtectedRoute permissions={[PERMISSIONS.CAMPAIGN_WINNER_VIEW]}>
                <CampaignWinner />
              </ProtectedRoute>
            } />
            <Route path="/campaigns" element={
              <ProtectedRoute permissions={[PERMISSIONS.CAMPAIGN_VIEW]}>
                <CampaignList />
              </ProtectedRoute>

            } />
            <Route path="/Audit" element={
              <ProtectedRoute permissions={[PERMISSIONS.ACTIVITY_LOG_VIEW]}>
                <Audit />
              </ProtectedRoute>

            } />
          
            <Route path="/reporting" element={
              <ProtectedRoute permissions={[PERMISSIONS.AUDIT_REPORT_VIEW]}>
                <Reporting />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute permissions={[PERMISSIONS.USER_VIEW]}>
                <User />
              </ProtectedRoute>
              
              } />
            <Route path="/roles" element={
              <ProtectedRoute permissions={[PERMISSIONS.ROLE_VIEW]}>
                <Role />
              </ProtectedRoute>
              } />
            <Route path="/upload" element={
              <ProtectedRoute permissions={[PERMISSIONS.TRX_UPLOAD_VIEW]}>
                <Import />
              </ProtectedRoute>
              } />
        
            <Route path="/templatemapping" element={
              <ProtectedRoute permissions={[PERMISSIONS.CSV_TEMPLATE_VIEW]}>
                <TemplateMapping />
              </ProtectedRoute>
             
              } />
            <Route path="/transaction" element={
              <ProtectedRoute permissions={[PERMISSIONS.TRANSACTION_VIEW]}>
                <Transaction />
              </ProtectedRoute>
              } />
            <Route path="/screenmaker" element={
              <ProtectedRoute permissions={[PERMISSIONS.TRX_UPLOAD_VIEW]}>
                <ScreenMaker />
              </ProtectedRoute>
              } />


              <Route path="/adconfig" element={
                <ProtectedRoute permissions={[PERMISSIONS.AD_CONFIG_VIEW]}>
                  <AdConfig />
                </ProtectedRoute>
              } />
              <Route path="/notifyconfig" element={
                <ProtectedRoute permissions={[PERMISSIONS.USER_CONFIG_VIEW]}>
                  <Notify />
                </ProtectedRoute>
              } />
              <Route path="/password-policy" element={
                <ProtectedRoute permissions={[PERMISSIONS.PASSWORD_POLICY_VIEW]}>
                  <PasswordPolicy />
                </ProtectedRoute>
              } />
              <Route path="/smtp-config" element={
                <ProtectedRoute permissions={[PERMISSIONS.NOTIFICATION_CONFIG_UPSERT, PERMISSIONS.NOTIFICATION_CONFIG_VIEW]}>
                  <SmtpConfig />
                </ProtectedRoute>
              } />
              <Route path="/blacklist" element={
                <ProtectedRoute permissions={[PERMISSIONS.BLACKLIST_VIEW]}>
                  <Blacklist />
                </ProtectedRoute>
              } />
              <Route path="/institutions" element={
                <ProtectedRoute permissions={[PERMISSIONS.INSTITUTION_VIEW]}>
                  <Institution />
                </ProtectedRoute>
              } />
              
          </>


        </Routes>
        </ForcePasswordChangeGuard>
      </ConditionalLayoutWrapper>
    </Router>
    </SidebarProvider>
  );
}

export default Routing;
